import { useCallback, useMemo, useState } from "react";

// Manages the ordered field_mappings jsonb array used by ExportJobMappingModal:
// [{type:'column', target_key, source_column}, {type:'static', target_key, value}]
// Mirrors the affiliate_exports.validate_field_mappings() DB trigger's rules
// client-side so the UI can show inline errors before a save round-trip.
let idSeq = 0;
function nextRowId() {
  idSeq += 1;
  return `row-${idSeq}`;
}

function makeColumnRow(sourceColumn) {
  return { rowId: nextRowId(), type: "column", target_key: sourceColumn, source_column: sourceColumn };
}

function makeStaticRow() {
  return { rowId: nextRowId(), type: "static", target_key: "", value: "" };
}

export function useFieldMappings(initialMappings = []) {
  const [mappings, setMappings] = useState(() =>
    (initialMappings || []).map((m) => ({ rowId: nextRowId(), ...m }))
  );

  // Replaces the whole mapping set with a fresh initial list, assigning new
  // rowIds — used when switching templates before a job is saved, where the
  // previous template's mappings must not carry over (see
  // ExportJobMappingModal's template-switch effect).
  const resetMappings = useCallback((newInitialMappings = []) => {
    setMappings((newInitialMappings || []).map((m) => ({ rowId: nextRowId(), ...m })));
  }, []);

  const addColumnMapping = useCallback((sourceColumn) => {
    if (!sourceColumn) return;
    setMappings((prev) => [...prev, makeColumnRow(sourceColumn)]);
  }, []);

  const addCustomField = useCallback(() => {
    setMappings((prev) => [...prev, makeStaticRow()]);
  }, []);

  const removeMapping = useCallback((rowId) => {
    setMappings((prev) => prev.filter((m) => m.rowId !== rowId));
  }, []);

  const updateMapping = useCallback((rowId, patch) => {
    setMappings((prev) => prev.map((m) => (m.rowId === rowId ? { ...m, ...patch } : m)));
  }, []);

  // rowId -> error message, for duplicate/empty target_key. Mirrors the
  // DB trigger's uniqueness-of-target_key and non-empty-key rules.
  const errors = useMemo(() => {
    const byKey = new Map();
    mappings.forEach((m) => {
      const key = (m.target_key || "").trim();
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(m.rowId);
    });
    const result = {};
    mappings.forEach((m) => {
      const key = (m.target_key || "").trim();
      if (!key) {
        result[m.rowId] = "Target key is required.";
        return;
      }
      const dupes = byKey.get(key) || [];
      if (dupes.length > 1) {
        result[m.rowId] = "Duplicate target key.";
      }
    });
    return result;
  }, [mappings]);

  const isValid = mappings.length > 0 && Object.keys(errors).length === 0;

  // Strips the client-only rowId before persisting — matches the DB shape.
  const toPersistShape = useCallback(() => {
    return mappings.map(({ rowId, ...rest }) => rest);
  }, [mappings]);

  return {
    mappings,
    setMappings,
    resetMappings,
    addColumnMapping,
    addCustomField,
    removeMapping,
    updateMapping,
    errors,
    isValid,
    toPersistShape,
  };
}
